import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Bell, ArrowLeft, FileText, User, Clock, Check, Trash2, Filter, ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { getAuthHeaders } from "@/utils/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NOTIF_ICONS = {
  new_case_file: FileText,
  new_user: User
};

const NOTIF_COLORS = {
  new_case_file: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30" },
  new_user: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" }
};

const NOTIF_LABELS = {
  new_case_file: "Stolen Case File",
  new_user: "New User"
};

export default function AdminNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const checkRes = await fetch(`${API}/admin/check`, { headers: getAuthHeaders() });
      if (!checkRes.ok) {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);
      await fetchNotifications();
    } catch {
      navigate("/dashboard");
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/notifications`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/admin/notifications/${notificationId}/read`, {
        method: "PUT", headers: getAuthHeaders()
      });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API}/admin/notifications/${notificationId}`, {
        method: "DELETE", headers: getAuthHeaders()
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success("Notification deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/admin/notifications/read-all`, {
        method: "PUT", headers: getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  const filtered = notifications.filter(n => {
    if (filter === "all") return true;
    if (filter === "unread") return !n.read;
    return n.type === filter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="back-to-admin-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              <span className="font-heading text-lg uppercase tracking-wider">Admin Notifications</span>
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white text-xs">{unreadCount} unread</Badge>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead} data-testid="admin-mark-all-read-btn">
              <Check className="w-4 h-4 mr-2" />
              Mark all read
            </Button>
          )}
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-5xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-border bg-card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total</p>
                <p className="text-2xl font-heading font-bold">{notifications.length}</p>
              </div>
              <Bell className="w-6 h-6 text-primary opacity-50" />
            </CardContent>
          </Card>
          <Card className="border-red-500/30 bg-card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Case Files</p>
                <p className="text-2xl font-heading font-bold text-red-400">
                  {notifications.filter(n => n.type === "new_case_file").length}
                </p>
              </div>
              <FileText className="w-6 h-6 text-red-400 opacity-50" />
            </CardContent>
          </Card>
          <Card className="border-blue-500/30 bg-card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">New Users</p>
                <p className="text-2xl font-heading font-bold text-blue-400">
                  {notifications.filter(n => n.type === "new_user").length}
                </p>
              </div>
              <User className="w-6 h-6 text-blue-400 opacity-50" />
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-lg uppercase tracking-wide text-muted-foreground">
            All Notifications ({filtered.length})
          </h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="admin-notif-filter-btn">
                <Filter className="w-4 h-4 mr-2" />
                {filter === "all" ? "All" : filter === "unread" ? "Unread" : NOTIF_LABELS[filter] || filter}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilter("all")}>All</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("unread")}>Unread Only</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("new_case_file")}>
                <FileText className="w-4 h-4 mr-2 text-red-400" />Case Files
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("new_user")}>
                <User className="w-4 h-4 mr-2 text-blue-400" />New Users
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-heading text-xl uppercase mb-2">No Notifications</h3>
              <p className="text-muted-foreground text-sm">Admin notifications will appear here when new users register or case files are created.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(notif => {
              const Icon = NOTIF_ICONS[notif.type] || Bell;
              const colors = NOTIF_COLORS[notif.type] || { bg: "bg-primary/10", text: "text-primary", border: "border-primary/30" };

              return (
                <Card
                  key={notif.notification_id}
                  className={`border-border bg-card ${!notif.read ? "border-primary/30" : ""}`}
                  data-testid={`admin-notif-card-${notif.notification_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className={`p-3 rounded-lg ${colors.bg} flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${colors.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{notif.title}</h3>
                              {!notif.read && (
                                <Badge className="bg-primary text-primary-foreground text-xs">NEW</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!notif.read && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => markAsRead(notif.notification_id)}
                                className="h-8 w-8 p-0"
                                data-testid={`admin-mark-read-${notif.notification_id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => deleteNotification(notif.notification_id)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                              data-testid={`admin-delete-${notif.notification_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(notif.created_at)}
                          </span>
                          <Badge variant="outline" className={`${colors.border} ${colors.text} text-xs`}>
                            {NOTIF_LABELS[notif.type] || notif.type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
